import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Circle, X, ArrowRight, Sparkles } from 'lucide-react';
import { useOnboarding } from '../lib/useOnboarding';

// Step metadata — order here drives the rendered list.
const STEPS = [
    {
        key: 'brand',
        title: 'Setează brand-ul restaurantului',
        helper: 'Adaugă logo-ul și numele restaurantului. Apar în meniul QR al clienților.',
        to: '/r/settings',
        cta: 'Mergi la setări',
        skipLabel: null,  // can be auto-completed; explicit skip not offered
    },
    {
        key: 'menu',
        title: 'Adaugă cel puțin 3 produse în meniu',
        helper: 'Creează o categorie și 3 produse cu preț. Așa au clienții ce comanda.',
        to: '/r/menu',
        cta: 'Mergi la meniu',
        skipLabel: null,
    },
    {
        key: 'table',
        title: 'Adaugă prima masă și generează QR-ul',
        helper: 'Fiecare masă primește un cod QR unic pe care clienții îl scanează.',
        to: '/r/tables',
        cta: 'Mergi la mese',
        skipLabel: null,
    },
    {
        key: 'scan_qr',
        title: 'Scanează QR-ul cu telefonul tău',
        helper: 'Deschide pagina Mese, descarcă PNG-ul QR-ului și scanează-l cu telefonul. Vezi meniul tău live, exact cum îl văd clienții.',
        to: '/r/tables',
        cta: 'Vezi QR-urile',
        skipLabel: 'Am scanat',
    },
    {
        key: 'invite_team',
        title: 'Invită un ospătar sau un bucătar',
        helper: 'Adaugă cel puțin un membru al echipei ca să poți testa fluxul comenzilor.',
        to: '/r/staff',
        cta: 'Mergi la echipă',
        skipLabel: 'Las pentru mai târziu',
    },
];

function StepRow({ step, status, onSkip }) {
    const done = status?.completed;
    const IconWrap = done ? Check : Circle;
    return (
        <li className={`onboarding-step${done ? ' onboarding-step--done' : ''}`}
            data-testid={`onboarding-step-${step.key}`}>
            <div className={`onboarding-step__check${done ? ' onboarding-step__check--done' : ''}`} aria-hidden="true">
                <IconWrap size={16} strokeWidth={done ? 3 : 1.6} />
            </div>
            <div className="onboarding-step__body">
                <div className="onboarding-step__title">{step.title}</div>
                {!done && <div className="onboarding-step__helper">{step.helper}</div>}
            </div>
            {!done && (
                <div className="onboarding-step__actions">
                    <Link to={step.to} className="btn btn-secondary btn-sm" data-testid={`onboarding-cta-${step.key}`}>
                        {step.cta} <ArrowRight size={14} />
                    </Link>
                    {step.skipLabel && (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onSkip(step.key)}
                                data-testid={`onboarding-skip-${step.key}`}>
                            {step.skipLabel}
                        </button>
                    )}
                </div>
            )}
        </li>
    );
}

export function OnboardingChecklist({ enabled = true }) {
    const { state, loading, completed, total, allDone, dismiss, skipStep } = useOnboarding({ enabled });

    if (!enabled || loading) return null;
    if (state.dismissed) return null;

    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

    if (allDone) {
        return (
            <div className="panel onboarding-card onboarding-card--done" data-testid="onboarding-done">
                <div className="onboarding-head">
                    <div>
                        <div className="kicker" style={{ color: 'var(--neon-teal)' }}>
                            <Sparkles size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                            Setup complet
                        </div>
                        <h3 className="headline" style={{ margin: '0.2rem 0 0', fontSize: '1.3rem' }}>
                            Gata! Toate setările inițiale sunt configurate.
                        </h3>
                    </div>
                    <button type="button" className="onboarding-dismiss" onClick={dismiss}
                            aria-label="Închide checklist" data-testid="onboarding-dismiss">
                        <X size={18} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="panel onboarding-card" data-testid="onboarding-checklist">
            <div className="onboarding-head">
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="kicker">Pași rapizi pentru a începe</div>
                    <h3 className="headline" style={{ margin: '0.2rem 0 0', fontSize: '1.35rem' }}>
                        Configurează restaurantul în 5 pași
                    </h3>
                    <div className="onboarding-progress" aria-label={`Progres ${completed} din ${total}`}>
                        <div className="onboarding-progress__bar" style={{ width: `${pct}%` }} />
                        <div className="onboarding-progress__label" data-testid="onboarding-progress">
                            {completed}/{total} completați
                        </div>
                    </div>
                </div>
                <button type="button" className="onboarding-dismiss" onClick={dismiss}
                        aria-label="Închide checklist" data-testid="onboarding-dismiss"
                        title="Închide (poți reactiva din setări)">
                    <X size={18} />
                </button>
            </div>
            <ul className="onboarding-steps">
                {STEPS.map((s) => (
                    <StepRow key={s.key} step={s} status={state.steps?.[s.key]} onSkip={skipStep} />
                ))}
            </ul>
        </div>
    );
}
